import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, IconButton, LinearProgress, List, ListItem, ListItemText,
  Paper, Stack, TextField, Typography,
} from "@mui/material";
import { MdAdd, MdCloudUpload, MdDelete, MdOpenInNew } from "react-icons/md";
import Colors from "../../assets/styles/Colors";
import {
  RESOURCE_CATEGORIES, addResourceItem, deleteResourceItem, getResourceCategory,
} from "../store/actions/resources";

// ── AddResourceDialog ─────────────────────────────────────────────────────────

function AddResourceDialog({ open, onClose, categoryKey, onSaved }) {
  const [name, setName]           = useState("");
  const [driveUrl, setDriveUrl]   = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState("");
  const coverRef = useRef();

  function reset() {
    setName(""); setDriveUrl(""); setCoverFile(null); setCoverPreview(null); setSaveError("");
  }
  function handleClose() { reset(); onClose(); }

  function handleCoverFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (coverPreview?.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  }

  async function handleSave() {
    if (!name.trim() || !driveUrl.trim()) return;
    setSaving(true);
    setSaveError("");
    try {
      await addResourceItem(categoryKey, { name: name.trim(), driveUrl: driveUrl.trim() }, coverFile);
      onSaved();
      handleClose();
    } catch (err) {
      setSaveError(err.message || "Error al guardar el recurso.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
    >
      <DialogTitle sx={{ color: Colors.textPrimary }}>Añadir recurso</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Nombre" value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth size="small" required
            sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }}
          />
          <TextField
            label="Link de Google Drive"
            value={driveUrl}
            onChange={(e) => setDriveUrl(e.target.value)}
            fullWidth size="small" required
            placeholder="https://drive.google.com/..."
            sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }}
          />
          <Divider sx={{ borderColor: Colors.borderSecondary }}>
            <Typography variant="caption" sx={{ color: Colors.textSecondary }}>Portada (opcional)</Typography>
          </Divider>
          <Box
            onClick={() => coverRef.current.click()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { setCoverFile(f); setCoverPreview(URL.createObjectURL(f)); } }}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              border: `2px dashed ${Colors.borderPrimary}`,
              borderRadius: 2,
              height: 110,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              overflow: "hidden",
              backgroundColor: Colors.bgTertiary,
              "&:hover": { borderColor: Colors.primary, backgroundColor: Colors.bgElevated },
              transition: "border-color 160ms, background 160ms",
            }}
          >
            {coverPreview ? (
              <img src={coverPreview} alt="cover" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <Stack alignItems="center" spacing={0.75}>
                <MdCloudUpload size={22} color={Colors.textSecondary} />
                <Typography variant="caption" sx={{ color: Colors.textSecondary }}>Imagen de portada</Typography>
              </Stack>
            )}
            <input ref={coverRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverFile} />
          </Box>
          {saveError && <Typography variant="body2" sx={{ color: Colors.error }}>{saveError}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: Colors.textSecondary }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim() || !driveUrl.trim()}
          sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
        >
          {saving ? "Subiendo..." : "Añadir"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── CategoryPanel ─────────────────────────────────────────────────────────────

function CategoryPanel({ category }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [addOpen, setAddOpen]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id }
  const [deleting, setDeleting]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getResourceCategory(category.key);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [category.key]);

  useEffect(() => { load(); }, [load]);

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteResourceItem(category.key, confirmDelete.id);
      setConfirmDelete(null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <AddResourceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        categoryKey={category.key}
        onSaved={load}
      />

      <Paper sx={{ borderRadius: 3, border: `1px solid ${Colors.borderSecondary}`, backgroundColor: Colors.bgSecondary, overflow: "hidden" }}>
        {/* Panel header */}
        <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${Colors.borderSecondary}`, backgroundColor: Colors.bgTertiary }}>
          <Typography variant="subtitle1" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
            {category.label}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<MdAdd />}
            onClick={() => setAddOpen(true)}
            sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
          >
            Añadir
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ "& .MuiLinearProgress-bar": { backgroundColor: Colors.primary } }} />}

        {!loading && items.length === 0 && (
          <Box sx={{ px: 2.5, py: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
              Sin recursos. Haz clic en Añadir.
            </Typography>
          </Box>
        )}

        {!loading && items.length > 0 && (
          <List dense disablePadding>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                {idx > 0 && <Divider sx={{ borderColor: Colors.borderSecondary }} />}
                <ListItem
                  sx={{ py: 1.25, px: 2 }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      {item.driveUrl && (
                        <IconButton
                          size="small"
                          component="a"
                          href={item.driveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Abrir en Drive"
                          sx={{ color: Colors.textSecondary }}
                        >
                          <MdOpenInNew size={15} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete({ id: item.id, name: item.name })}
                        title="Eliminar"
                        sx={{ color: Colors.error }}
                      >
                        <MdDelete size={15} />
                      </IconButton>
                    </Stack>
                  }
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mr: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 42, height: 42, borderRadius: 1,
                        flexShrink: 0, overflow: "hidden",
                        backgroundColor: Colors.bgTertiary,
                        border: `1px solid ${Colors.borderSecondary}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {item.coverImageUrl ? (
                        <img src={item.coverImageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <MdCloudUpload size={16} color={Colors.textMuted} />
                      )}
                    </Box>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ color: Colors.textPrimary }} noWrap>
                          {item.name || "Sin nombre"}
                        </Typography>
                      }
                      secondary={
                        item.driveUrl ? (
                          <Typography variant="caption" sx={{ color: Colors.textSecondary, display: "block", maxWidth: 160 }} noWrap>
                            {item.driveUrl}
                          </Typography>
                        ) : null
                      }
                    />
                  </Stack>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Confirm delete dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleting && setConfirmDelete(null)}
        PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
      >
        <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: Colors.textSecondary }}>
            ¿Eliminar "{confirmDelete?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting} sx={{ color: Colors.textSecondary }}>
            Cancelar
          </Button>
          <Button onClick={confirmDeleteAction} disabled={deleting} sx={{ color: Colors.error }}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── ResourcesView ─────────────────────────────────────────────────────────────

export default function ResourcesView() {
  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Recursos</Typography>
          <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
            Materiales y documentos de Google Drive por categoría
          </Typography>
        </Paper>

        <Grid container spacing={2.5}>
          {RESOURCE_CATEGORIES.map((cat) => (
            <Grid item xs={12} md={6} xl={4} key={cat.key}>
              <CategoryPanel category={cat} />
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
}
